import { ArrowLeft } from "lucide-react";

export function PrivacyPolicy({ onBack }) {
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 mb-6 transition-colors"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </button>

        <div className="bg-white border border-zinc-200 p-6 sm:p-10">
          <h1
            className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-1 tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm text-zinc-400 mb-8">Last updated: August 13, 2026</p>

          <div className="space-y-6 text-sm text-zinc-700 leading-relaxed">
            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">1. Introduction</h2>
              <p>
                Pallet Cut Calculator ("we," "our," or "us") is operated by Michael Fisher. This Privacy Policy explains how we collect, use, and protect your information when you use the Pallet Cut Calculator application (the "App"). By using the App, you agree to the practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">2. Information We Collect</h2>
              <p className="mb-2">We collect only the information necessary to provide the App's functionality:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Account Information:</strong> Email address, name, and password (stored as a secure hash — we never store your actual password).</li>
                <li><strong>Pallet Data:</strong> Pallet type configurations, components, dimensions, lumber entries, and related calculations you create within the App.</li>
                <li><strong>Cut List Data:</strong> Saved list items, stopwatch times, check states, notes, cut history, and print history.</li>
                <li><strong>Lumber Inventory Data:</strong> Lumber types, counts, and adjustment history you record.</li>
                <li><strong>Order Data:</strong> Customer names, order details, delivery dates, notes, timers, and files or photos you attach to orders.</li>
                <li><strong>Photos & Files:</strong> Images and file attachments you upload, stored on our servers.</li>
                <li><strong>Billing Information:</strong> If you subscribe, your subscription plan and payment status. Card payments are processed by <strong>Stripe</strong> — we never see or store your full card number. We store only a Stripe customer reference and subscription status.</li>
                <li><strong>Device Information:</strong> A basic device identifier, browser type, and platform for devices signed in to your account, used to show you (and support) which devices are using the account.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">3. Information We Do NOT Collect</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>We do <strong>not</strong> use Google Analytics, Firebase Analytics, Crashlytics, or any third-party analytics or tracking services.</li>
                <li>We do <strong>not</strong> collect location data or browsing history. IP addresses are used only transiently for login rate-limiting (brute-force protection) and are not used for tracking.</li>
                <li>We do <strong>not</strong> use cookies for advertising or tracking. The only cookies we set are secure <strong>authentication cookies</strong> that keep you signed in.</li>
                <li>We do <strong>not</strong> sell, rent, or share your personal information with third parties for their own purposes.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">4. How We Use Your Information</h2>
              <p>We use your information primarily to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Authenticate your account and provide secure access.</li>
                <li>Store and sync your pallet configurations, cut lists, inventory, and orders across your devices.</li>
                <li>Enable real-time collaboration and multi-device sync.</li>
                <li>Process subscription payments and manage your billing status (via Stripe).</li>
                <li>Send you emails such as password resets, account notices, and product updates (via Resend).</li>
              </ul>
              <p className="mt-2">We also process information as reasonably needed for security, abuse prevention, billing and account administration, backups, and legal compliance — the same operational purposes described in our Terms of Service.</p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">4a. Third-Party Service Providers</h2>
              <p className="mb-2">We share the minimum data necessary with these processors, solely to operate the App:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Stripe</strong> (payments): receives your email and payment details to process subscriptions. See Stripe's privacy policy at stripe.com/privacy.</li>
                <li><strong>Resend</strong> (email delivery): receives your email address to deliver password-reset and account-notice emails.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">5. Data Storage & Security</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your data is stored in a secure database with industry-standard protections.</li>
                <li>Passwords are stored only as secure one-way hashes — never in plain text.</li>
                <li>Sign-in sessions use secure, encrypted tokens transmitted over HTTPS.</li>
                <li>Uploaded images and file attachments are access-restricted to the account that uploaded them.</li>
                <li>We implement brute-force protection on login attempts, and changing your password signs out all other sessions.</li>
                <li>Database backups are generated automatically (with password data removed) so your data can be restored if something goes wrong.</li>
              </ul>
              <p className="mt-2">We may update the specific technologies we use over time; we will always maintain security practices at least as protective as those described here.</p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">6. Local Device Data</h2>
              <p>
                The App may store some data locally on your device through your browser's local storage to keep it fast and responsive. This data remains on your device and is synced to our servers when the App is connected. You can clear this data at any time through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">7. Data Retention & Deletion</h2>
              <p>
                Your data is retained for as long as your account is active. You may delete your account at any time through the App's Settings menu. Deleted accounts can be restored by logging back in within 10 days; after that, all associated data — including pallet types, cut lists, orders, inventory, print history, and uploaded images and files — is permanently removed from our active systems. Deleted data may persist in routine backups for a limited additional period before those backups are cycled out. Payment records held by Stripe are retained as required for accounting and legal purposes.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">8. Children's Privacy</h2>
              <p>
                The App is a business tool intended for use by adults. You must be at least 18 years old to create an account, as stated in our Terms of Service. We do not knowingly collect personal information from anyone under 18.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be reflected by updating the "Last updated" date at the top of this page. Continued use of the App after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">10. Contact</h2>
              <p>
                If you have any questions about this Privacy Policy or your data, please contact:
              </p>
              <p className="mt-2">
                <strong>Michael Fisher</strong><br />
                <a href="mailto:Michaelervinfisher2002@gmail.com" className="text-orange-600 hover:underline">
                  Michaelervinfisher2002@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
